// MobileHomeTab.jsx — mobile-only reimplementation of the Flutter mobile app's
// Home screen (swipeable job deck + floating bottom nav + matched header).
// Only renders when (isMobile && activeTab === "console").
import { useState, useRef, useEffect, useMemo, useCallback } from "react"
import { Plus, Settings, Clock, Bookmark, BookmarkCheck, MapPin, Briefcase, Sparkles, Reply, ArrowRight, SlidersHorizontal } from "lucide-react"
import toast from "react-hot-toast"
import { daysAgoLabel, initials } from "../JobCard"
import MobileBottomNav from "./MobileBottomNav"

const FF = '"Plus Jakarta Sans", "Inter", -apple-system, sans-serif'
const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"

// ── Flutter mobile-app palette ──────────────────────────────────────────────
const INK          = "#0F172A"
const INK_FAINT    = "#64748B"
const INK_SUBTLE   = "#94A3B8"
const LIGHT_BG     = "#FFFFFF"
const SURFACE_ALT  = "#F3F2ED"
const BORDER_SOFT  = "#E6E5DF"

const CARD_VARIANTS = [
  ["#FFE4D6", "#FBD1B8"], // peach
  ["#DDF3E4", "#C4E9D2"], // mint
  ["#E6DEFB", "#D1C2F5"], // lavender
  ["#DCEBFB", "#C1DAF3"], // sky
  ["#FFF1C4", "#FCE49A"], // butter
  ["#FCDDE5", "#F7C1D0"], // rose
]

function formatSeniority(key = "") {
  switch (key.toUpperCase()) {
    case "SENIOR":    return "Senior"
    case "MID":       return "Mid-level"
    case "JUNIOR":    return "Junior"
    case "LEAD":      return "Lead"
    case "PRINCIPAL": return "Principal"
    case "ENTRY":     return "Entry"
    case "INTERN":    return "Intern"
    default: return key ? key.charAt(0) + key.slice(1).toLowerCase() : ""
  }
}

function CompanyLogo({ logoUrl, company, size = 52 }) {
  const [bfErr, setBfErr] = useState(false)
  const [fbErr, setFbErr] = useState(false)
  const domain = company
    ? company.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "") + ".com"
    : null
  const bfUrl = domain ? `https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}` : null
  const src = !bfErr ? bfUrl : (!fbErr && logoUrl ? logoUrl : null)
  const onErr = () => { if (!bfErr) setBfErr(true); else setFbErr(true) }

  const base = {
    width: size, height: size, borderRadius: 12, flexShrink: 0,
    background: "#fff",
    border: `1px solid ${INK}0F`,
    objectFit: "contain",
  }
  if (src) {
    return <img src={src} alt={company} onError={onErr} loading="lazy" style={base} />
  }
  return (
    <div style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ fontSize: size * 0.3, fontWeight: 800, color: INK, letterSpacing: -0.5 }}>
        {initials(company)}
      </span>
    </div>
  )
}

function SkillChip({ label }) {
  return (
    <div style={{
      padding: "5px 11px",
      background: "rgba(31,41,55,0.08)",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 500,
      color: INK_FAINT,
      whiteSpace: "nowrap",
    }}>
      {label}
    </div>
  )
}

function MatchLine({ score }) {
  const color = score >= 70 ? "#16A34A" : score >= 45 ? "#CA8A04" : "#DC2626"
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <Sparkles size={13} color={color} strokeWidth={2} />
      <span style={{ fontSize: 12, fontWeight: 700, color, letterSpacing: -0.1 }}>
        {score}% match
      </span>
    </div>
  )
}

// ── A single job card (collapsed view) ──────────────────────────────────────
function JobDeckCard({ job, variant, bookmarked, onToggleBookmark }) {
  const title    = job.role || job.title || "Untitled role"
  const company  = job.company || "Company"
  const location = (job.canonical_location_id || job.location || "")
  const ago      = daysAgoLabel(job)
  const seniority = formatSeniority(job?.normalization?.seniority_key)
  const taxonomy = job?.jobTaxonomy || job?.job_taxonomy || ""
  const expMin   = job?.experience_min ?? job?.experienceMin
  const matchScore = Math.round(job?.match_score || job?.matchScore || 0)
  const skills = Array.isArray(job.skills) ? job.skills.slice(0, 8) : []
  const salary = job.salary || ""
  const description = job.description || ""
  const isNew = job?.is_new || job?.isNew

  const prettyLocation = location
    ? location.charAt(0).toUpperCase() + location.slice(1).toLowerCase()
    : ""

  return (
    <div style={{
      height: "100%",
      width: "100%",
      background: `linear-gradient(135deg, ${variant[0]}, ${variant[1]})`,
      borderRadius: 24,
      border: `1px solid ${INK}0D`,
      boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
      padding: "20px 20px 28px",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      {/* Header: logo + company + bookmark */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <CompanyLogo logoUrl={job.company_logo} company={company} size={52} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 900, letterSpacing: 0.8, color: INK,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {company}
          </div>
          {(ago || isNew) && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
              {ago && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Clock size={11} color={INK_SUBTLE} strokeWidth={2} />
                  <span style={{ fontSize: 11, fontWeight: 500, color: INK_FAINT }}>{ago}</span>
                </div>
              )}
              {isNew && (
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: "#16A34A" }}>
                  ★ NEW
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleBookmark(job) }}
          aria-label={bookmarked ? "Unsave" : "Save"}
          style={{
            width: 34, height: 34, border: "none", background: "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", padding: 0,
          }}
        >
          {bookmarked
            ? <BookmarkCheck size={24} color={INK} strokeWidth={2} fill={INK} />
            : <Bookmark size={24} color={INK_SUBTLE} strokeWidth={2} />
          }
        </button>
      </div>

      {/* Title */}
      <h3 style={{
        margin: "20px 0 0",
        fontSize: 28,
        fontWeight: 800,
        letterSpacing: "-0.03em",
        color: INK,
        lineHeight: 1.15,
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}>
        {title}
      </h3>

      {(seniority || taxonomy) && (
        <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: INK_FAINT, letterSpacing: 0.1 }}>
          {[seniority, taxonomy].filter(Boolean).join("  ·  ")}
        </div>
      )}

      {(matchScore > 0 || prettyLocation || expMin != null) && (
        <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {matchScore > 0 && <MatchLine score={matchScore} />}
          {(prettyLocation || expMin != null) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              {prettyLocation && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                  <MapPin size={12} color={INK_SUBTLE} strokeWidth={2} />
                  <span style={{
                    fontSize: 11, fontWeight: 500, color: INK_FAINT,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{prettyLocation}</span>
                </div>
              )}
              {prettyLocation && expMin != null && (
                <span style={{ width: 3, height: 3, borderRadius: "50%", background: INK_SUBTLE }} />
              )}
              {expMin != null && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Briefcase size={12} color={INK_SUBTLE} strokeWidth={2} />
                  <span style={{ fontSize: 11, fontWeight: 500, color: INK_FAINT }}>
                    {expMin}+ yrs
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {skills.length > 0 && (
        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 94, overflow: "hidden" }}>
          {skills.map((s, i) => <SkillChip key={i} label={s} />)}
        </div>
      )}

      {salary && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.4, color: INK }}>{salary}</div>
          <div style={{ fontSize: 12, color: INK_SUBTLE }}>per year</div>
        </div>
      )}

      {description && (
        <>
          <div style={{ marginTop: 26, fontSize: 11, fontWeight: 700, color: INK_FAINT, letterSpacing: 0.8, textTransform: "uppercase" }}>
            About the role
          </div>
          <p style={{
            marginTop: 14,
            fontSize: 13, color: INK_FAINT, lineHeight: 1.6,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>
            {description.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}
          </p>
        </>
      )}

      <div style={{ marginTop: "auto", paddingTop: 20, fontSize: 11, color: INK_SUBTLE }}>
        Swipe to skip or apply
      </div>
    </div>
  )
}

// ── Loading / empty / error placeholders ────────────────────────────────────
function CardShell({ children, variant = CARD_VARIANTS[0] }) {
  return (
    <div style={{
      height: "100%", width: "100%",
      background: `linear-gradient(135deg, ${variant[0]}, ${variant[1]})`,
      borderRadius: 24,
      border: `1px solid ${INK}0D`,
      boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
      padding: 24,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      gap: 10,
    }}>
      {children}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────
export default function MobileHomeTab({
  jobs,
  loading,
  error,
  savedJobIds,
  onHeartToggle,
  onApply,
  onPass,
  onTabChange,
  pendingApplications,
  onOpenFilters,
  onOpenAddResume,
  activeTab,
  swipesUsed = 0,
  swipeLimit = 10,
}) {
  const [cardIndex, setCardIndex] = useState(0)
  const [drag, setDrag] = useState({ dx: 0, dy: 0, dragging: false })
  const [exiting, setExiting] = useState(null) // 'left' | 'right' | null
  const [lastSkipped, setLastSkipped] = useState(null)
  const pointerStart = useRef(null)
  const swipesLeft = Math.max(0, swipeLimit - swipesUsed)
  const filterCount = 0 // placeholder — parent-driven count can be wired later

  // Reset index when jobs reload (new fetch)
  useEffect(() => { setCardIndex(0); setDrag({ dx: 0, dy: 0, dragging: false }); setExiting(null) }, [jobs?.length === 0 ? 0 : jobs?.[0]?.id || jobs?.[0]?.job_id])

  const currentJob = jobs && cardIndex < jobs.length ? jobs[cardIndex] : null
  const nextJob    = jobs && cardIndex + 1 < jobs.length ? jobs[cardIndex + 1] : null
  const nextNext   = jobs && cardIndex + 2 < jobs.length ? jobs[cardIndex + 2] : null
  const variant     = CARD_VARIANTS[cardIndex % CARD_VARIANTS.length]
  const nextVariant = CARD_VARIANTS[(cardIndex + 1) % CARD_VARIANTS.length]
  const nextNextVariant = CARD_VARIANTS[(cardIndex + 2) % CARD_VARIANTS.length]

  const onPointerDown = (e) => {
    if (exiting) return
    pointerStart.current = { x: e.clientX, y: e.clientY, t: Date.now() }
    setDrag({ dx: 0, dy: 0, dragging: true })
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch {}
  }
  const onPointerMove = (e) => {
    if (!drag.dragging || !pointerStart.current) return
    const dx = e.clientX - pointerStart.current.x
    const dy = (e.clientY - pointerStart.current.y) * 0.3
    setDrag({ dx, dy, dragging: true })
  }

  const commitSkip = useCallback((job) => {
    if (!job) return
    setLastSkipped(job)
    onPass?.(job)
  }, [onPass])

  const commitApply = useCallback((job) => {
    if (!job) return
    setLastSkipped(null)
    onApply?.(job)
  }, [onApply])

  const onPointerUp = () => {
    if (!drag.dragging) return
    const w = window.innerWidth || 360
    const threshold = w * 0.3
    const { dx } = drag
    pointerStart.current = null
    if (dx > threshold) {
      // Apply
      const job = currentJob
      setExiting("right")
      setTimeout(() => {
        setExiting(null)
        setDrag({ dx: 0, dy: 0, dragging: false })
        setCardIndex((i) => i + 1)
        commitApply(job)
      }, 240)
    } else if (dx < -threshold) {
      // Skip
      const job = currentJob
      setExiting("left")
      setTimeout(() => {
        setExiting(null)
        setDrag({ dx: 0, dy: 0, dragging: false })
        setCardIndex((i) => i + 1)
        commitSkip(job)
      }, 240)
    } else {
      setDrag({ dx: 0, dy: 0, dragging: false })
    }
  }

  // Auto-dismiss undo
  useEffect(() => {
    if (!lastSkipped) return
    const t = setTimeout(() => setLastSkipped(null), 2200)
    return () => clearTimeout(t)
  }, [lastSkipped])

  const undoSkip = () => {
    if (cardIndex === 0) return
    setLastSkipped(null)
    setCardIndex((i) => Math.max(0, i - 1))
  }

  // Transform math — mirror Flutter deck transform
  const w = typeof window !== "undefined" ? window.innerWidth : 360
  const dxDisplay = exiting === "left" ? -w * 1.5 : exiting === "right" ? w * 1.5 : drag.dx
  const dyDisplay = exiting ? drag.dy * 0.5 : drag.dy
  const angle = (dxDisplay / w) * 14 // deg
  const absProgress = Math.min(1, Math.abs(drag.dx) / w)
  const rightHint = Math.max(0, Math.min(1, drag.dx / 120))
  const leftHint  = Math.max(0, Math.min(1, -drag.dx / 120))

  const topInset = "calc(14px + env(safe-area-inset-top))"
  const bottomNavHeight = 72 // approximate — gives card deck room
  const deckToNavGap = 28     // breathing room between card bottom and nav bar

  return (
    <div
      className="mobile-home-root"
      style={{
        position: "fixed",
        inset: 0,
        background: LIGHT_BG,
        display: "flex",
        flexDirection: "column",
        zIndex: 150,
        fontFamily: FF,
        color: INK,
      }}
    >
      {/* Header */}
      <div style={{
        padding: `${topInset} 16px 4px`,
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexShrink: 0,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 30, fontWeight: 800, letterSpacing: "-0.5px",
            color: INK, lineHeight: 1.1,
          }}>Jobs</div>
          <div style={{ fontSize: 14, color: "#64748B", fontWeight: 400 }}>
            Matched to your profile
          </div>
        </div>
        <button
          onClick={onOpenAddResume}
          aria-label="Add a job link"
          style={{
            width: 44, height: 44, borderRadius: 999,
            background: SURFACE_ALT, border: "none",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: INK, cursor: "pointer",
          }}
        >
          <Plus size={22} strokeWidth={2} />
        </button>
        <button
          onClick={onOpenFilters}
          aria-label="Filters"
          style={{
            width: 44, height: 44, borderRadius: 999,
            background: SURFACE_ALT, border: "none",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: INK, cursor: "pointer",
          }}
        >
          <Settings size={22} strokeWidth={2} />
        </button>
      </div>

      {/* Toolbar: AI search + swipes-left pill + filter icon */}
      <div style={{
        padding: "10px 16px 4px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
      }}>
        <button
          onClick={onOpenFilters}
          aria-label="Search jobs or ask AI"
          style={{
            flex: 1, minWidth: 0,
            height: 44,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "0 12px",
            background: "#fff",
            border: `1px solid ${BORDER_SOFT}`,
            borderRadius: 12,
            cursor: "pointer",
            fontFamily: FF,
            color: INK_FAINT,
            textAlign: "left",
          }}
        >
          <Sparkles size={17} strokeWidth={2} color={INK_FAINT} style={{ flexShrink: 0 }} />
          <span style={{
            flex: 1, minWidth: 0,
            fontSize: 14,
            color: INK_SUBTLE,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            Search jobs or ask AI
          </span>
          <ArrowRight size={16} strokeWidth={2} color={INK_FAINT} style={{ flexShrink: 0 }} />
        </button>

        <button
          type="button"
          onClick={() => toast(`You're granted ${swipeLimit} free job applications per day`)}
          aria-label={`${swipesLeft} applies left today`}
          style={{
            height: 44,
            padding: "0 12px",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: SURFACE_ALT,
            border: "none",
            borderRadius: 12,
            fontFamily: FF,
            color: INK,
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
            cursor: "pointer",
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 800 }}>{swipesLeft}</span>
          <span style={{ fontSize: 12, fontWeight: 500, color: INK_FAINT }}>left</span>
        </button>

        <button
          onClick={onOpenFilters}
          aria-label="Filters"
          style={{
            position: "relative",
            width: 44, height: 44, borderRadius: 12,
            background: SURFACE_ALT,
            border: "none",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: INK, cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <SlidersHorizontal size={20} strokeWidth={2} />
          {filterCount > 0 && (
            <span style={{
              position: "absolute",
              top: -4, right: -4,
              minWidth: 16, height: 16,
              padding: "0 4px",
              background: "#F97316",
              color: "#fff",
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 800,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}>{filterCount}</span>
          )}
        </button>
      </div>

      {/* Deck area */}
      <div style={{
        flex: 1,
        position: "relative",
        padding: "8px 12px 12px",
        paddingBottom: bottomNavHeight + deckToNavGap,
        minHeight: 0,
      }}>
        {/* Loading state */}
        {loading && (!jobs || jobs.length === 0) && (
          <CardShell>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              border: `3px solid rgba(15,23,42,0.1)`,
              borderTopColor: INK,
              animation: "mhome-spin 0.8s linear infinite",
            }} />
            <div style={{ fontSize: 13, color: INK_FAINT, marginTop: 6 }}>Finding roles for you…</div>
          </CardShell>
        )}

        {/* Error */}
        {!loading && error && (!jobs || jobs.length === 0) && (
          <CardShell>
            <div style={{ fontSize: 16, fontWeight: 700, color: INK }}>Couldn't load jobs</div>
            <div style={{ fontSize: 13, color: INK_FAINT }}>Check your connection and try again.</div>
          </CardShell>
        )}

        {/* Empty / all caught up */}
        {!loading && !error && (!jobs || jobs.length === 0 || cardIndex >= jobs.length) && (
          <CardShell>
            <div style={{ fontSize: 16, fontWeight: 700, color: INK }}>
              {jobs && jobs.length > 0 ? "All caught up" : "No jobs found"}
            </div>
            <div style={{ fontSize: 13, color: INK_FAINT }}>
              {jobs && jobs.length > 0 ? "Check back later for new listings" : "Try adjusting the filters"}
            </div>
          </CardShell>
        )}

        {/* Card deck */}
        {currentJob && (
          <div style={{ position: "absolute", inset: "8px 12px 12px", bottom: bottomNavHeight + deckToNavGap }}>
            {/* Ghost card (index + 2) */}
            {nextNext && (
              <div style={{
                position: "absolute",
                top: 16 - absProgress * 8,
                left: 14 - absProgress * 7,
                right: 14 - absProgress * 7,
                bottom: -16 + absProgress * 8,
                background: `linear-gradient(135deg, ${nextNextVariant[0]}, ${nextNextVariant[1]})`,
                borderRadius: 24,
                border: `1px solid ${INK}0A`,
                boxShadow: "0 8px 18px rgba(15,23,42,0.06)",
              }} />
            )}

            {/* Next card (index + 1) rises as front leaves */}
            {nextJob && (
              <div style={{
                position: "absolute",
                top: 8 - absProgress * 8,
                left: 7 - absProgress * 7,
                right: 7 - absProgress * 7,
                bottom: -8 + absProgress * 8,
              }}>
                <JobDeckCard
                  job={nextJob}
                  variant={nextVariant}
                  bookmarked={savedJobIds?.has(nextJob.id || nextJob.job_id)}
                  onToggleBookmark={() => {}}
                />
              </div>
            )}

            {/* Front card (swipeable) */}
            <div
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              style={{
                position: "absolute", inset: 0,
                transform: `translate3d(${dxDisplay}px, ${dyDisplay}px, 0) rotate(${angle}deg)`,
                transition: exiting ? "transform 240ms cubic-bezier(0.4, 0, 1, 1)"
                           : drag.dragging ? "none"
                           : "transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                touchAction: "pan-y",
                cursor: drag.dragging ? "grabbing" : "grab",
              }}
            >
              <JobDeckCard
                job={currentJob}
                variant={variant}
                bookmarked={savedJobIds?.has(currentJob.id || currentJob.job_id)}
                onToggleBookmark={onHeartToggle}
              />

              {/* Swipe hint overlays */}
              {rightHint > 0 && (
                <div style={{
                  position: "absolute", top: 24, left: 20,
                  padding: "6px 12px",
                  border: "2px solid #16A34A",
                  borderRadius: 8,
                  color: "#16A34A",
                  fontSize: 16, fontWeight: 900, letterSpacing: 1,
                  opacity: rightHint,
                  pointerEvents: "none",
                }}>APPLY</div>
              )}
              {leftHint > 0 && (
                <div style={{
                  position: "absolute", top: 24, right: 20,
                  padding: "6px 12px",
                  border: "2px solid #DC2626",
                  borderRadius: 8,
                  color: "#DC2626",
                  fontSize: 16, fontWeight: 900, letterSpacing: 1,
                  opacity: leftHint,
                  pointerEvents: "none",
                }}>SKIP</div>
              )}
            </div>

            {/* Undo skip pill */}
            {lastSkipped && !drag.dragging && !exiting && (
              <div style={{
                position: "absolute",
                left: 0, right: 0, bottom: -8,
                display: "flex", justifyContent: "center",
                pointerEvents: "none",
              }}>
                <button
                  onClick={undoSkip}
                  style={{
                    pointerEvents: "auto",
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "13px 20px",
                    background: INK,
                    color: "#fff",
                    border: "none",
                    borderRadius: 30,
                    fontSize: 15, fontWeight: 600, letterSpacing: -0.2,
                    boxShadow: "0 4px 16px rgba(15,23,42,0.18)",
                    cursor: "pointer",
                    fontFamily: FF,
                  }}
                >
                  <Reply size={18} strokeWidth={2} />
                  Undo skip
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        pendingApplications={pendingApplications || 0}
      />

      <style>{`
        @keyframes mhome-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}
